import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Text,
  Spinner,
  VStack,
  Alert,
  AlertIcon,
  Badge,
  Image,
  Button,
  useToast,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import { supabase } from "../utils/supabaseClient";

export default function CouponPage() {
  const { postcode } = useParams();
  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState([]);
  const [error, setError] = useState(null);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchCoupons() {
      const { data, error } = await supabase
        .from("coupons")
        .select(
          `
          *,
          businesses (
            name,
            logo_url,
            brand_color,
            tagline
          )
        `
        )
        .eq("postcode", postcode.toUpperCase())
        .gte("expiry", new Date().toISOString())
        .order("expiry", { ascending: true });

      if (error) {
        setError(error.message);
      } else {
        setCoupons(data || []);
      }

      setLoading(false);
    }

    fetchCoupons();
  }, [postcode]);

  const handleClaimClick = async (couponId) => {
    await supabase.from("coupon_clicks").insert({
      coupon_id: couponId,
      clicked_at: new Date().toISOString(),
      source: "couponPage",
      postcode: postcode.toUpperCase(),
    });

    toast({
      title: "Redirecting...",
      description: "Opening coupon claim page",
      status: "info",
      duration: 2000,
      isClosable: true,
    });

    navigate(`/coupon/${couponId}`);
  };

  const canonicalUrl = `https://postcode.coupons/coupons/${postcode.toUpperCase()}`;
  const pageTitle = `Coupons for ${postcode.toUpperCase()} | Local Deals & Discounts`;

  return (
    <>
      <Helmet>
        {/* BASIC SEO */}
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={`Browse all active local coupons available in postcode ${postcode.toUpperCase()}. View discounts, expiry dates, and claim offers instantly.`}
        />
        <link rel="canonical" href={canonicalUrl} />

        {/* OPEN GRAPH */}
        <meta property="og:title" content={pageTitle} />
        <meta
          property="og:description"
          content={`See all active coupons and local offers available in postcode ${postcode.toUpperCase()}.`}
        />
        <meta property="og:image" content="https://postcode.coupons/og/coupon.png" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />

        {/* TWITTER */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta
          name="twitter:description"
          content={`Browse all active coupons available in postcode ${postcode.toUpperCase()}.`}
        />
        <meta name="twitter:image" content="https://postcode.coupons/og/coupon.png" />

        {/* STRUCTURED DATA — Search Results Page */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `Coupons for ${postcode.toUpperCase()}`,
            url: canonicalUrl,
            description: `Browse all active coupons available in postcode ${postcode.toUpperCase()}.`,
            hasPart: coupons.map((c) => ({
              "@type": "Offer",
              name: c.title,
              price: "0",
              priceCurrency: "GBP",
              availability:
                new Date(c.expiry) < new Date()
                  ? "https://schema.org/Expired"
                  : "https://schema.org/InStock",
              validThrough: c.expiry,
              seller: {
                "@type": "LocalBusiness",
                name: c.businesses.name,
                image: c.businesses.logo_url,
              },
              url: `https://postcode.coupons/coupon/${c.id}`,
            })),
          })}
        </script>
      </Helmet>

      <Box p={8}>
        <Text fontSize="2xl" fontWeight="bold" mb={4}>
          🎁 Coupons for {postcode.toUpperCase()}
        </Text>

        {loading && <Spinner size="xl" mt={8} />}

        {error && (
          <Alert status="error" mt={8}>
            <AlertIcon />
            ❌ Error loading coupons: {error}
          </Alert>
        )}

        {!loading && !error && coupons.length === 0 && (
          <Text color="gray.500">No active coupons found for this postcode.</Text>
        )}

        {!loading && !error && coupons.length > 0 && (
          <VStack spacing={6} align="stretch">
            {coupons.map((coupon) => (
              <Box
                key={coupon.id}
                border="1px solid #ccc"
                borderRadius="md"
                p={4}
                bg="white"
                boxShadow="sm"
              >
                <Image
                  src={coupon.businesses.logo_url}
                  alt="Logo"
                  boxSize="60px"
                  borderRadius="full"
                  mb={2}
                />

                <Text fontSize="lg" fontWeight="bold">
                  {coupon.title}
                </Text>

                <Text fontSize="sm" color="gray.600">
                  {coupon.businesses.name} • {coupon.businesses.tagline}
                </Text>

                <Text mt={2}>💸 {coupon.discount}% off</Text>

                <Text fontSize="sm" color="gray.500">
                  Expires: {new Date(coupon.expiry).toLocaleDateString()}
                </Text>

                <Badge mt={2} colorScheme="green">
                  {coupon.claims || 0} claimed
                </Badge>

                <Button
                  size="sm"
                  colorScheme="blue"
                  mt={3}
                  onClick={() => handleClaimClick(coupon.id)}
                >
                  Claim Now
                </Button>
              </Box>
            ))}
          </VStack>
        )}
      </Box>
    </>
  );
}
